import type { HelpGuide } from '@types';

export const CLIENT_HELP_CONTENT: Record<string, HelpGuide> = {
    'client-start': {
        reportType: 'HelpGuide',
        title: 'Start here: your client dashboard',
        introduction: 'Use your client account to request services, follow progress, and read delivered work.',
        sections: [
            {
                title: 'Sign in or reset your password',
                content: `1. Open the client portal and enter your account email and password.
2. Complete the sign-in verification using an enrolled security method.
3. If setup is requested, follow the offered SMS or authenticator steps and verify the code.
4. If you forgot your password, select **Forgot Password?**, enter your account email, and send the reset link.
5. Check your inbox and use the link to set your password before signing in again.

If you lose a security method, try another enrolled method. Use the sign-in screen’s support option when none are available.`
            },
            {
                title: 'Find your way around',
                content: `1. Sign in using your organization account.
2. Check the organization name on **Home** before opening work.
3. Open the left navigation using the menu button if it is hidden. Hover over an icon to see its name.
4. Choose **Intelligence Library** to read delivered work, or **Service Requests** to request work when that option is available.
5. Choose **Help & Guidance** whenever you need instructions or support.

Your menu follows your assigned role. Ask your organization owner or administrator about a missing option.`
            }
        ]
    },
    'organizationDashboard': {
        reportType: 'HelpGuide',
        title: 'Home: check requests and delivered work',
        introduction: 'See your organization’s recent activity, assigned consultant, and country coverage.',
        sections: [
            {
                title: 'Check your organization',
                content: `1. Choose **Home** in the left navigation.
2. Check **Active Requests**, **Delivered This Month**, **Pending Invoice**, and **Total Reports**.
3. Read **Recent Requests** to find the request reference, status, and consultant.
4. Select an available summary card or **View all** to open the related page.
5. Check **My Assigned Consultant** and the agreement or trial information before requesting more work.`
            },
            {
                title: 'Open country information',
                content: `1. Select a country card on Home.
2. Review its country dashboard, reports, and service activity.
3. Return to **Home** to select another country.

Country coverage follows your organization’s locations and delivered country services. Contact your consultant when the coverage needs changing.`
            }
        ]
    },
    'client-country': {
        reportType: 'HelpGuide',
        title: 'Country dashboards and daily briefs',
        introduction: 'Read the latest available country brief and compare it with earlier reports.',
        sections: [
            {
                title: 'Read a country brief',
                content: `1. Select a country card on **Home**.
2. Find the daily brief and check its report date and availability.
3. Select **View report** when a brief is available.
4. Read the findings, affected areas, and recommended actions.
5. Select **Past briefs** to open an earlier available report.

If a report is unavailable, use the date and status shown rather than assuming the information is current.`
            },
            {
                title: 'Review country activity',
                content: `1. Review the country dashboard’s service requests and delivered work.
2. Open an available report title or delivery file.
3. Review the country live feeds for additional context.
4. Ask your consultant about a finding through **Help & Guidance**.

Live feeds provide context; they do not replace the reviewed report.`
            }
        ]
    },
    'reportLibrary': {
        reportType: 'HelpGuide',
        title: 'Intelligence Library: open reports and files',
        introduction: 'Find work delivered to your organization without running analysis tools.',
        sections: [
            {
                title: 'Open delivered work',
                content: `1. Choose **Intelligence Library** in the left navigation.
2. Find the service or saved report you need.
3. Select **View Report** or an available report title to read it.
4. Select **Open Product** beside an attached delivery file to open or download it.
5. Check the report’s date, scope, and approval details before using it.

An empty library means no completed intelligence products are available yet. Ask your consultant about the request’s delivery status.`
            },
            {
                title: 'Save an offline copy',
                content: `1. Open the delivered report or file.
2. Use its available PDF or download control.
3. Wait for the file to finish opening or downloading.
4. Check your browser’s Downloads folder and confirm all pages are readable.

If nothing opens, allow the site’s blocked download or new tab and try again. Share the request reference with support if the file still fails.`
            }
        ]
    },
    'serviceRequests': {
        reportType: 'HelpGuide',
        title: 'Service Requests: create and track a request',
        introduction: 'Tell your consultant what you need, save unfinished work, and follow delivery.',
        sections: [
            {
                title: 'Submit a new request',
                content: `1. Choose **Service Requests** in the left navigation.
2. Select the required service from the available products.
3. Enter the country or region, priority, delivery date, and description requested by the form.
4. Complete the service-specific details. Use complete addresses and explain the decision or problem the work should support.
5. Upload the supporting documents requested by the form, or record unavailable documents where that option is provided.
6. Select **Save Draft** if you need to finish later.
7. Review the information and select **Submit Request**.
8. Find the request in **My Requests** and note its reference.

If submission is disabled, check required fields, document requirements, consultant assignment, and any trial limit shown on the page.`
            },
            {
                title: 'Follow progress',
                content: `1. Find your request in **My Requests**.
2. Read its status, consultant, and review information.
3. Open **View conversation** when a message needs your attention.
4. Respond when more information is requested.
5. When work is delivered, open its report, file, or dashboard from the request or **Intelligence Library**.

Saving a draft does not submit it. A submitted request still needs consultant review before delivery.`
            }
        ]
    },
    'client-request-conversation': {
        reportType: 'HelpGuide',
        title: 'Service Requests: answer questions and upload evidence',
        introduction: 'Respond to your consultant’s requests for facts or supporting documents.',
        sections: [
            {
                title: 'Send the requested information',
                content: `1. Open **Service Requests** and find the request reference.
2. Select **View conversation**, or **Open advance checks** for an advance survey.
3. Read the consultant’s message and earlier responses.
4. Enter the requested facts in **Your response** when the response form is available.
5. Select **Upload Evidence** for supporting PDF or DOCX files. Follow the size and file-count limits shown.
6. Select **Resubmit Evidence**, or **Regenerate** when the page offers that action.
7. Check the new status and return later for the consultant’s reply.

Regenerated work needs consultant approval again. Use **Retry Evidence Review** or **Retry Regeneration** only when that recovery option appears.`
            }
        ]
    },
    'client-fsat': {
        reportType: 'HelpGuide',
        title: 'Facility assessments: view FSAT documents',
        introduction: 'Find documents linked to a facility security assessment request.',
        sections: [
            {
                title: 'Review the assessment documents',
                content: `1. Open **Service Requests** and find the facility assessment request.
2. Select **View FSAT Documents** after the request has been submitted.
3. Review the available documents and their status.
4. Open an available file to read the assessment or supporting material.
5. Return to the request conversation to provide missing evidence or ask a question.

An unavailable document is not a completed delivery. Check the request status or ask your assigned consultant.`
            },
            {
                title: 'Supply or update assessment documents',
                content: `1. Open **View FSAT Documents** and check whether document editing is available.
2. Upload each available document under the matching document type.
3. Use **We do not have this document** for a required document your organization cannot supply.
4. Check earlier uploads and assign a document type where requested.
5. Select **Save Documents** after all required document slots are addressed.
6. Check the saved confirmation and tell your consultant about the update.

If the documents are locked, use the request conversation to ask your consultant how to provide a revision.`
            }
        ]
    },
    'client-srm-framework': {
        reportType: 'HelpGuide',
        title: 'Delivered SRM dashboards: review your framework',
        introduction: 'Read a delivered security framework and its risk register.',
        sections: [
            {
                title: 'Review a delivered dashboard',
                content: `1. Open the delivered service in **Service Requests** or **Intelligence Library**.
2. Select **Open SRM Dashboard** when a dashboard is included.
3. Check the framework’s status, location, component readiness, and risk score.
4. Read **Framework Components** to understand the delivered work.
5. Read **Risk Register** to review the recorded threats and actions.
6. Send corrections or questions to your consultant with the request reference.

Client framework dashboards are read-only. Your consultant updates the underlying framework.`
            }
        ]
    },
    'global_risk_register': {
        reportType: 'HelpGuide',
        title: 'Master Risk Register: review organization risks',
        introduction: 'Review recorded risks across your organization’s security frameworks.',
        sections: [
            {
                title: 'Find a risk to discuss',
                content: `1. Choose **Home**, then **Master Risk Register**.
2. Review the summary counts and visible risk entries.
3. Use the available search or filters to narrow the list.
4. Read the threat description, likelihood, impact, score, status, and remediation information.
5. Share the risk and related framework reference with your consultant when an update is needed.

Client access is read-only. A risk score helps prioritize review; read the supporting description before choosing an action.`
            }
        ]
    },
    'client-assets': {
        reportType: 'HelpGuide',
        title: 'Global assets: view registered locations',
        introduction: 'Check the facilities and other assets recorded for your organization.',
        sections: [
            {
                title: 'Check an asset',
                content: `1. Select **View Global Assets** at the bottom of the left navigation.
2. Find the asset you want to review.
3. Check its location, type, criticality, and notes.
4. Check the **Primary Profile** marker when a primary location has been set.
5. Contact your consultant with the asset name and corrected details if anything is wrong.

Client asset access is read-only.`
            }
        ]
    },
    'subscriptionManagement': {
        reportType: 'HelpGuide',
        title: 'Billing & Invoices: review charges and your MSA',
        introduction: 'Organization owners and administrators can review billing and agreement details.',
        sections: [
            {
                title: 'Review your agreement and invoices',
                content: `1. Choose **Billing & Invoices** in the left navigation.
2. Review the subscription or trial status, dates, and annual pricing when shown.
3. Select **View Signed MSA** when the signed Master Services Agreement is available.
4. Review **Invoices** and **Total Due**, including the currency for each balance.
5. Select **Download PDF** beside an available invoice document.
6. Select the linked invoice number when online payment is available, then follow the checkout instructions.

Check the agreement, trial, and invoice information separately. A service invoice may still be due during Test Mode.`
            },
            {
                title: 'Complete trial agreement and payment setup',
                content: `1. Check the **Free trial** notice on **My Account** or **Billing & Invoices**.
2. Read the trial end date and the current payment setup status.
3. Select **Sign MSA & Set Up Payment**, or **Set Up Payment** if your agreement is already signed.
4. Review the agreement and complete the signature steps offered by the form.
5. Follow the payment setup prompts and check the resulting confirmation.
6. Return to billing and check the status before the trial ends.

If payment setup is already scheduled, use **View Billing** when offered. Staff client previews disable payment changes.`
            },
            {
                title: 'Schedule cancellation',
                content: `1. Open **Billing & Invoices** and find the available subscription cancellation control.
2. Read the confirmation and the access or trial end date.
3. Select **Schedule Cancellation** only when you want to stop future renewals.
4. Check the cancellation date shown after the request succeeds.

If the action is unavailable, ask your organization owner or support. Cancellation and payment controls are disabled in a staff client preview.`
            }
        ]
    },
    'myAccount': {
        reportType: 'HelpGuide',
        title: 'My Account: manage users and delivery responsibility',
        introduction: 'Organization owners and administrators manage team access from My Account.',
        sections: [
            {
                title: 'Invite a colleague',
                content: `1. Choose **My Account** in the left navigation.
2. Check the **Profile** and **Organization** details.
3. Under **Organization Users**, select **Add User**.
4. Enter the colleague’s full name and email address.
5. Choose the role that matches their work.
6. Select **Send Invitation** and check the confirmation.

Only owners can assign the administrator role. Service request access and learning access depend on the user’s role or granted permissions.`
            },
            {
                title: 'Change team access',
                content: `1. Find the colleague under **Organization Users**.
2. Use the available role, status, or service-request access controls.
3. Wait for the update to finish and check the new value.
4. Ask the colleague to sign in again if their menu needs refreshing.

Some owner and administrator controls are restricted to owners. Staff client previews disable account changes.`
            },
            {
                title: 'Assign an internal security professional',
                content: `1. Find **Service Delivery Responsibility** on My Account.
2. Select **Client has an internal security professional** when this applies.
3. Choose the active user who will prepare delivery forms.
4. Select **Save Responsibility**.
5. Tell the selected colleague which requests need preparation.

Internal professionals prepare forms where that option is available. IntelOptics consultants review and publish the work.`
            }
        ]
    },
    'client-ep-project': {
        reportType: 'HelpGuide',
        title: 'Executive Protection projects: itinerary and delivered package',
        introduction: 'Follow a protection project and read the approved operations package.',
        sections: [
            {
                title: 'Open and review a project',
                content: `1. Open **Service Requests** and find the EP Project Planner request.
2. Select **View Project**.
3. Choose **Project overview** to check the itinerary, venue addresses, contacts, and linked intelligence.
4. If address editing is available, enter the complete venue address and select its **Save venue address** control.
5. Open **Operations package** after the consultant publishes it.
6. Open **After Action Review** when the reviewed document becomes available.
7. Use an available **Download** button for delivered files.

Changing an address refreshes the work and requires another consultant review before release.`
            },
            {
                title: 'Send an observation or feedback',
                content: `1. Find **Report a security observation** when the project offers it.
2. Describe the observation clearly and select **Send observation**.
3. After the review is published, use **After Action Review feedback** when available.
4. Choose a project rating, write the feedback, and select **Submit feedback**.

Your assigned consultant receives these submissions. Staff client previews disable changes.`
            }
        ]
    },
    'client-advance-checks': {
        reportType: 'HelpGuide',
        title: 'Advance site surveys: record ground checks',
        introduction: 'Record what your advance team has physically verified.',
        sections: [
            {
                title: 'Complete an advance check',
                content: `1. Find the advance survey in **Service Requests**.
2. Select **Open advance checks**.
3. Read the consultant’s message and **Advance team ground checks**.
4. Select a check’s status: **Open**, **In progress**, **Verified**, or **Blocked**.
5. Enter the supporting facts in **Evidence or limitation**. A verified check needs evidence.
6. Select **Save check** and confirm the saved time.
7. Repeat for each check your team has assessed.

Unfinished and blocked checks remain visible in the report. Do not mark an unchecked item as verified. Staff client previews show these checks without allowing edits.`
            }
        ]
    },
    'client-travel-security': {
        reportType: 'HelpGuide',
        title: 'Travel security: trip workspace and alerts',
        introduction: 'Review your trip, approved travel assessment, and validated alerts.',
        sections: [
            {
                title: 'Review your travel brief',
                content: `1. Find the travel request in **Service Requests**.
2. Select **Travel Brief Dashboard**.
3. Check the monitoring status, trip window, and current risk.
4. Review the assessment, traveller details, emergency contacts, destinations, travel legs, accommodation, and medical providers.
5. Read **What changed** before relying on an earlier assessment.
6. Open the documents view and select **Open PDF** for available reviewed exports.

An unavailable PDF is not a completed export. Use the published workspace and ask your consultant about missing documents.`
            },
            {
                title: 'Respond to an alert',
                content: `1. Open the workspace’s alerts view.
2. Read the validated alert and **Recommended action**.
3. Select **Acknowledge** when you have reviewed it, or **Escalate** when consultant attention is needed.
4. Review **Notification settings** and choose available in-app or email notifications.
5. Select **Save notifications**.

These controls are disabled in a staff client preview. Check the monitoring status before expecting new alerts.`
            }
        ]
    },
    'client-lms': {
        reportType: 'HelpGuide',
        title: 'Learning Management Suite: find and read training',
        introduction: 'Owners, administrators, trainers, and learners can access the learning suite.',
        sections: [
            {
                title: 'Open learning material',
                content: `1. Choose **Learning Management Suite** in the left navigation when available.
2. Open the course or module available to your account.
3. Choose a lesson from the curriculum.
4. Read the lesson and use its available video, audio, or assessment controls.
5. Return to the curriculum to continue with the next lesson.

Your learning role determines the controls shown. Ask your organization administrator about a missing course or learning access.`
            }
        ]
    },
    'client-account-security': {
        reportType: 'HelpGuide',
        title: 'Account security: backup methods and email changes',
        introduction: 'Keep sign-in access current from your user menu.',
        sections: [
            {
                title: 'Add a backup security method',
                content: `1. Open your user menu and choose **Account security** when shown.
2. Review the enrolled security methods.
3. Select **Add backup or replacement method**.
4. Complete the offered SMS or authenticator setup and verify the code.
5. Sign in again using a verified method when prompted.
6. Remove an old method only after the replacement is working.

Your last security method cannot be removed. If all methods are lost, use the screen’s verified recovery support link. Account security changes are unavailable in staff client previews.`
            },
            {
                title: 'Change your account email',
                content: `1. Open **Account security** and find **Change email**.
2. Enter the new email address.
3. Select **Verify new email**, follow the verification prompts, and retrieve the email code.
4. Enter the code and select **Change email and sign out**.
5. Sign in again with the updated email.

Keep access to your existing sign-in and security methods while completing the change.`
            }
        ]
    },
    'help_guidance': {
        reportType: 'HelpGuide',
        title: 'Help & Guidance: instructions, chat, and support',
        introduction: 'Find instructions or contact the right support channel.',
        sections: [
            {
                title: 'Find instructions',
                content: `1. Choose **Help & Guidance** in the left navigation.
2. Select **Help Library**.
3. Open the client instruction manual or search for the screen or task.
4. Select a topic and follow its numbered steps.
5. Close the library to return to Help & Guidance.`
            },
            {
                title: 'Contact your consultant or support',
                content: `1. Find the consultant chat section on **Help & Guidance**.
2. Select **New Chat** or open an existing chat.
3. Choose an available contact and send the question with your request reference.
4. For specialist guidance, select **Industry Help & Support** and complete the ticket form.
5. For a broken screen, failed download, or access problem, select **Application IT Help & Support** and describe what happened.
6. Check **My Helpdesk Tickets** for updates when that section is shown.

Support submissions and chats are disabled in a staff client preview. If no consultant contact is available, check the assignment shown on Home.`
            },
            {
                title: 'Ask the assistant or share a suggestion',
                content: `1. Enter a question in **AI Chat** about your dashboard, delivered intelligence, or service request.
2. Use the answer to locate the next step, and contact your consultant for a decision about delivered work.
3. Select **Feedback** to send a product suggestion when available.
4. To review sign-in protection, open your user menu and choose **Account security** when shown.
5. Choose **Logout** from your user menu when finished.`
            }
        ]
    }
};
